import React, { FC, useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useTheme } from "@mui/material";
import {
  DoubleMaterialityESRSAreaEnum,
  DoubleMaterialityIroTypeEnum,
} from "@green0meter/api-client";

import StakeholdersSelection from "./components/StakeholdersSelection";
import TopicsSelection from "./components/TopicsSelection";
import IroSelection from "./components/IroSelection";
import QuestionsTable from "./components/QuestionsTable";
import ConnectorsOverlay from "./components/ConnectorsOverlay";
import { AssignStakeholdersDrawer } from "./components/AssignStakeholdersDrawer";
import {
  toggleAll,
  toggleInSet,
  type StringSet,
} from "./AssignStakeholders.helpers";
import { AssignLevel, createMakePathLabel } from "./makePathLabel";
import { createFilterBySelections } from "./filterBySelections";
import { useESRSLabels } from "../Stakeholders/components/useESRSLabels";

import { useGetQuestionAnswers } from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import { useGetQuestions, useGetStakeholders, useGetAssessments } from "@api";
import { useUserStore } from "@store/user";
import { Box, Stack, Typography } from "@components/base";
import { getYearFromSearchParams } from "@helpers";

type AssignContext = {
  level: AssignLevel;
  labelPath: string;
  questionIds: string[];
};

const DMAssignStakeholders: FC = () => {
  const { t } = useTranslation();
  const { getEsrsLabelFromEnum } = useESRSLabels();
  const theme = useTheme();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);

  const { questions, refetchQuestions } = useGetQuestions(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });
  const allQuestions = (questions?.data ?? []).filter((q) => q.enabled !== false);

  const { stakeholders } = useGetStakeholders(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });
  const allStakeholders = stakeholders?.data ?? [];

  const { assessments } = useGetAssessments(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });
  const assessmentTopicMap = new Map(
    (assessments?.data ?? []).map((a) => [a.id ?? "", a.topic]),
  );

  const [selectedIro, setSelectedIro] = useState<StringSet>(new Set());
  const { answers, refetch: refetchAnswers } = useGetQuestionAnswers(user);
  const [selectedStakeholderType, setSelectedStakeholderType] = useState<string>("");
  const [selectedEsrs, setSelectedEsrs] = useState<StringSet>(new Set());

  const [isAssignDrawerOpen, setIsAssignDrawerOpen] = useState(false);
  const [assignContext, setAssignContext] = useState<AssignContext | null>(null);
  const [selectedQuestions, setSelectedQuestions] = useState<StringSet>(new Set());

  const iroOptions: DoubleMaterialityIroTypeEnum[] = [
    DoubleMaterialityIroTypeEnum.Impact,
    DoubleMaterialityIroTypeEnum.Risk,
    DoubleMaterialityIroTypeEnum.Opportunity,
  ];
  const stakeholderTypeOptions: string[] = ["internal", "external"];
  const esrsOptions: DoubleMaterialityESRSAreaEnum[] = Object.values(
    DoubleMaterialityESRSAreaEnum,
  ).filter((v): v is DoubleMaterialityESRSAreaEnum => typeof v === "number");

  const [questionAssignments, setQuestionAssignments] = useState<
    Record<string, string[]>
  >({});

  const openAssignDrawer = (ctx: AssignContext): void => {
    setAssignContext(ctx);
    setIsAssignDrawerOpen(true);
  };

  const closeAssignDrawer = (): void => setIsAssignDrawerOpen(false);

  const makePathLabel = createMakePathLabel(
    t,
    selectedStakeholderType ? new Set([selectedStakeholderType]) : new Set(),
    selectedEsrs,
    (id) => getEsrsLabelFromEnum(Number(id)),
  );

  const filterBySelections = createFilterBySelections(
    selectedIro,
    selectedStakeholderType ? new Set([selectedStakeholderType]) : new Set(),
    selectedEsrs,
    assessmentTopicMap,
  );

  const filteredQuestions = filterBySelections(allQuestions);

  const allFilteredQuestionIds = filteredQuestions
    .map((q) => q.id ?? "")
    .filter(Boolean) as string[];

  useEffect(() => {
    if (allStakeholders.length === 0 || allFilteredQuestionIds.length === 0) {
      setQuestionAssignments({});

      return;
    }

    let isCancelled = false;

    const load = async (): Promise<void> => {
      const rows = (answers ?? []) as Array<{
        stakeholderId?: string;
        questionId?: string;
      }>;

      const stakeIdToName = new Map(
        allStakeholders.map((s) => [s.id, s.fullName] as const),
      );
      const questionIdToNames = new Map<string, string[]>();

      rows.forEach((row) => {
        const qid = row.questionId ?? "";
        const sid = row.stakeholderId ?? "";

        if (!qid || !sid) return;

        const name = stakeIdToName.get(sid);

        if (!name) return;

        const arr = questionIdToNames.get(qid) ?? [];

        arr.push(name);
        questionIdToNames.set(qid, arr);
      });

      if (isCancelled) return;

      const map: Record<string, string[]> = {};

      allFilteredQuestionIds.forEach((qid) => {
        map[qid] = questionIdToNames.get(qid) ?? [];
      });

      setQuestionAssignments(map);
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [answers, allStakeholders, allFilteredQuestionIds.join(",")]);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const sectionRefs = {
    stakeholders: useRef<HTMLDivElement | null>(null),
    topics: useRef<HTMLDivElement | null>(null),
    iro: useRef<HTMLDivElement | null>(null),
    questions: useRef<HTMLDivElement | null>(null),
  };

  return (
    <Box ref={containerRef} sx={{ position: "relative" }}>
      <ConnectorsOverlay
        containerRef={containerRef}
        sectionRefs={sectionRefs}
        stroke={
          (theme.palette.grey && theme.palette.grey[400]) ||
          theme.palette.divider ||
          theme.palette.primary.light
        }
        topicsCount={esrsOptions.length}
        iroCount={iroOptions.length}
        questionsCount={filteredQuestions.length}
      />

      <Stack spacing={2} sx={{ position: "relative", zIndex: 2 }}>
        <Typography variant="h6">
          {t("doubleMateriality.steps.questionAssignment", "Question assignment")}
        </Typography>

        <StakeholdersSelection
          sectionRef={sectionRefs.stakeholders}
          allQuestions={allQuestions}
          stakeholderTypeOptions={stakeholderTypeOptions}
          selectedStakeholderType={selectedStakeholderType}
          onToggle={(v) =>
            setSelectedStakeholderType((prev) => (prev === v ? "" : v))
          }
          onAssign={(level, label) => {
            const questionsToAssign =
              selectedQuestions.size > 0
                ? Array.from(selectedQuestions)
                : (filteredQuestions
                  .map((q) => q.id ?? "")
                  .filter(Boolean) as string[]);

            openAssignDrawer({
              level,
              labelPath: makePathLabel(level, label),
              questionIds: questionsToAssign,
            });
          }}
        />

        <TopicsSelection
          sectionRef={sectionRefs.topics}
          allQuestions={allQuestions}
          esrsOptions={esrsOptions}
          selectedEsrs={selectedEsrs}
          assessmentTopicMap={assessmentTopicMap}
          onToggle={(v) => toggleInSet(v, selectedEsrs, setSelectedEsrs)}
          onToggleAll={() =>
            toggleAll(esrsOptions.map(String), selectedEsrs, setSelectedEsrs)
          }
          isAllSelected={selectedEsrs.size === esrsOptions.length}
          onAssign={(level, label) => {
            const questionsToAssign =
              selectedQuestions.size > 0
                ? Array.from(selectedQuestions)
                : (filteredQuestions
                  .map((q) => q.id ?? "")
                  .filter(Boolean) as string[]);

            openAssignDrawer({
              level,
              labelPath: makePathLabel(level, label),
              questionIds: questionsToAssign,
            });
          }}
          selectedStakeholderType={selectedStakeholderType}
        />

        <IroSelection
          sectionRef={sectionRefs.iro}
          allQuestions={allQuestions}
          iroOptions={iroOptions}
          selectedIro={selectedIro}
          onToggle={(v) => toggleInSet(v, selectedIro, setSelectedIro)}
          onToggleAll={() =>
            toggleAll(iroOptions.map(String), selectedIro, setSelectedIro)
          }
          isAllSelected={selectedIro.size === iroOptions.length}
          onAssign={(level, label) => {
            const questionsToAssign =
              selectedQuestions.size > 0
                ? Array.from(selectedQuestions)
                : (filteredQuestions
                  .map((q) => q.id ?? "")
                  .filter(Boolean) as string[]);

            openAssignDrawer({
              level,
              labelPath: makePathLabel(level, label),
              questionIds: questionsToAssign,
            });
          }}
          selectedStakeholderType={selectedStakeholderType}
        />

        <QuestionsTable
          questions={filteredQuestions}
          allStakeholdersCount={allStakeholders.length}
          selectedQuestions={selectedQuestions}
          setSelectedQuestions={setSelectedQuestions}
          questionAssignments={questionAssignments}
          assessments={assessments?.data ?? []}
        />

        {assignContext ? (
          <AssignStakeholdersDrawer
            open={isAssignDrawerOpen}
            onClose={closeAssignDrawer}
            labelTitle={assignContext.labelPath}
            questionIds={assignContext.questionIds}
            questions={allQuestions}
            onSaved={() => {
              refetchQuestions();
              refetchAnswers();
              setSelectedQuestions(new Set());
            }}
          />
        ) : null}
      </Stack>
    </Box>
  );
};

export default DMAssignStakeholders;
